import axiosService from './axios-service';
import { LogCriteria, LogDownloadInfo } from '../dtos/search-criteria.dto';
import { LogsType } from '../utils/enums';
import { dateFormat, LOGS_FORMAT } from '../utils/datesUtils';
import { lowerCase } from 'lodash';
import {CUSTOM_TIME_RANGE} from '@src/utils/constants';

class LogsService {
    public getLogs = async (customerId: number, serviceId: number, logType: string, searchDto: LogCriteria): Promise<any> => {
        const body = {
            'skip': 0,
            'limit': 0,
            'logDetails': {
                'logType': this.convertLogType(logType),
                'fromDateTime': searchDto.timeDetails?.startDate,
                'toDateTime': searchDto.timeDetails?.endDate,
                'customRange': searchDto.timeDetails?.timeRange === CUSTOM_TIME_RANGE,
                'direction': this.getDropdownValue(searchDto.direction!),
                'sourceIp': this.getValue(searchDto.sourceIp),
                'destIp': this.getValue(searchDto.destIp),
                'dport': this.getValue(searchDto.dport),
                'action': this.getDropdownValue(searchDto.action!),
                'url': this.getValue(searchDto.url),
                'timeZoneId': Intl.DateTimeFormat().resolvedOptions().timeZone,
                'application': this.getValue(searchDto.application),
                'threatTypes': searchDto.threatTypes?.map(_ => lowerCase(_)),
                'rule': this.getValue(searchDto.rule) === 'All' ? undefined : this.getValue(searchDto.rule),
            }
        };
        const result = await axiosService.post(`/api/insights/logs/customer/${customerId}/service/${serviceId}/display`, body, false);
        return result?.data;
    };

    private getValue(val?: string) {
        return val || undefined;
    }

    private getDropdownValue(value: string) {
        return value === 'All' ? undefined : value?.toLowerCase();
    }

    private convertLogType(logType: string): string {
        const lowerCaseLogType = logType.toLowerCase();
        if (lowerCaseLogType === 'url') {
            return 'url_filtering';
        }
        return lowerCaseLogType;
    }

    public getCurrentDateForLogs = (): string => {
        return dateFormat(new Date(), LOGS_FORMAT);
    };

    public downloadLogs = async (customerId: number,
        serviceId: number,
        exportMethod: string,
        exportAll: LogsType[],
        searchDtoByType: Record<LogsType, LogCriteria>): Promise<void> => {
        const logsDownloadInfo: LogDownloadInfo[] = [];

        exportAll?.forEach(type => {
            const searchCriteriaDto = searchDtoByType[type];
            const logDownloadInfo = this.convertSearchCriteriaToDto(exportMethod, type, searchCriteriaDto);
            logsDownloadInfo.push(logDownloadInfo);
        });
        const body = {
            'logsDownloadInfo': logsDownloadInfo
        };
        await axiosService.post(`/api/insights/logs/customer/${customerId}/service/${serviceId}/download`, body);
    };

    private getDescription(type: LogsType, searchCriteria: LogCriteria): string {
        const result: string[] = [];
        for (const key in searchCriteria) {
            const value = (searchCriteria as any)[key];
            if (key === 'timeDetails') {
                result.push(`startTime=${searchCriteria.timeDetails?.startDate?.toLocaleDateString()}`,
                    `endTime=${searchCriteria.timeDetails?.endDate?.toLocaleDateString()}`);
            } else if (key === 'rule') {
                if (value !== 'All') {
                    result.push(`rule=${value}`);
                }
            }
            else if (value !== undefined) {
                result.push(`${key}=${value}`);
            }
        }
        return `${type}: ${result.join(', ')}`;
    }

    private convertSearchCriteriaToDto(exportMethod: string, logType: LogsType, searchCriteria: LogCriteria) {
        return {
            logsFileType: exportMethod,
            description: this.getDescription(logType, searchCriteria),
            logDetails: {
                logType: this.convertLogType(logType),
                fromDateTime: searchCriteria.timeDetails?.startDate,
                toDateTime: searchCriteria.timeDetails?.endDate,
                customRange: searchCriteria.timeDetails?.timeRange === CUSTOM_TIME_RANGE,
                direction: this.getDropdownValue(searchCriteria.direction!),
                url: this.getValue(searchCriteria.url),
                sourceIp: this.getValue(searchCriteria.sourceIp),
                destIp: this.getValue(searchCriteria.destIp),
                dport: this.getValue(searchCriteria.dport),
                action: this.getDropdownValue(searchCriteria.action!),
                timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
                application: this.getValue(searchCriteria.application),
                threatTypes: searchCriteria.threatTypes?.length ? searchCriteria.threatTypes.map(_=>_.toLowerCase()) : undefined,
                rule: this.getValue(searchCriteria.rule) === 'All' ? undefined : this.getValue(searchCriteria.rule),
            }
        };
    }

    public downloadLogsFile = async (customerId: number, serviceId: number, filename: string): Promise<Blob> => {
        const response = await axiosService.get(
            `/api/insights/logs/customer/${customerId}/service/${serviceId}/${filename}`,
            true,
            { responseType: 'blob' }
        );
        return response.data;
    };
}


const logsService = new LogsService();
export default logsService;
